import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';

import instaAvatar1 from '../../../assets/assets-home/img/insta-avatar/1.webp';
import instaAvatar2 from '../../../assets/assets-home/img/insta-avatar/2.webp';
import instaAvatar3 from '../../../assets/assets-home/img/insta-avatar/3.webp';
import instaAvatar4 from '../../../assets/assets-home/img/insta-avatar/4.webp';
import instaAvatar5 from '../../../assets/assets-home/img/insta-avatar/5.webp';

// Load images from each instagram folder using webpack's require.context
function loadImages(ctx: __WebpackModuleApi.RequireContext): string[] {
  return ctx
    .keys()
    .sort((a, b) => {
      const numA = parseInt(a.match(/(\d+)/)?.[1] ?? '0');
      const numB = parseInt(b.match(/(\d+)/)?.[1] ?? '0');
      return numA - numB;
    })
    .map((key) => ctx(key) as string);
}

const folder1Images = loadImages(
  require.context('../../../assets/assets-home/img/instagram/1', false, /\.webp$/)
);
const folder2Images = loadImages(
  require.context('../../../assets/assets-home/img/instagram/2', false, /\.webp$/)
);
const folder3Images = loadImages(
  require.context('../../../assets/assets-home/img/instagram/3', false, /\.jpg$/)
);
const folder4Images = loadImages(
  require.context('../../../assets/assets-home/img/instagram/4', false, /\.webp$/)
);
const folder5Images = loadImages(
  require.context('../../../assets/assets-home/img/instagram/5', false, /\.jpg$/)
);

interface SlideData {
  nameKey: string;
  handle: string;
  location: string;
  instagramUrl: string;
  avatar: string;
  images: string[];
}

const slides: SlideData[] = [
  {
    nameKey: 'instagram.people.masha',
    handle: '@masha_lobanovaa',
    location: "Villa d'Este",
    instagramUrl: 'https://instagram.com/masha_lobanovaa',
    avatar: instaAvatar1,
    images: folder1Images,
  },
  {
    nameKey: 'instagram.people.carina',
    handle: '@carina',
    location: 'The Lana by Dorchester Collection',
    instagramUrl: 'https://instagram.com/carina',
    avatar: instaAvatar2,
    images: folder2Images,
  },
  {
    nameKey: 'instagram.people.sergey',
    handle: '@seregabunya',
    location: 'Habitas AlUla',
    instagramUrl: 'https://instagram.com/seregabunya',
    avatar: instaAvatar4,
    images: folder3Images,
  },
  {
    nameKey: 'instagram.people.karina',
    handle: '@karinaross',
    location: 'Abbaye des Vaux de Cernay',
    instagramUrl: 'https://www.instagram.com/karinaross/',
    avatar: instaAvatar5,
    images: folder4Images,
  },
  {
    nameKey: 'instagram.people.nour',
    handle: '@nourelhaaj',
    location: 'Nolinski Paris',
    instagramUrl: 'https://instagram.com/nourelhaaj',
    avatar: instaAvatar3,
    images: folder5Images,
  },
];

const ArrowIcon = () => (
  <svg className="w-3 h-3 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.33337 10H16.6667M16.6667 10L11.6667 5M16.6667 10L11.6667 15"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VerifiedBadge = () => (
  <svg
    className="lg:w-6 lg:h-6 sm:w-5 sm:h-5 h-2.5 w-2.5 text-[#1DA1F2]"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" />
  </svg>
);

/** Navigation arrows placed inside <Swiper> — uses the official useSwiper() hook */
const SliderNavButtons = () => {
  const swiper = useSwiper();
  const [isBeginning, setIsBeginning] = useState(swiper.isBeginning);
  const [isEnd, setIsEnd] = useState(swiper.isEnd);

  useEffect(() => {
    const updateState = (s: SwiperClass) => {
      setIsBeginning(s.isBeginning);
      setIsEnd(s.isEnd);
    };

    swiper.on('slideChange', updateState);
    swiper.on('reachBeginning', updateState);
    swiper.on('reachEnd', updateState);
    swiper.on('fromEdge', updateState);

    return () => {
      swiper.off('slideChange', updateState);
      swiper.off('reachBeginning', updateState);
      swiper.off('reachEnd', updateState);
      swiper.off('fromEdge', updateState);
    };
  }, [swiper]);

  return (
    <>
      {!isBeginning && (
        <button
          onClick={() => swiper.slidePrev()}
          className="absolute left-2 sm:left-[24px] top-1/2 -translate-y-1/2 z-10 sm:w-8 sm:h-8 w-5 h-5 rounded-full bg-[#EFE8DF] flex items-center justify-center cursor-pointer"
        >
          <div className="rotate-180">
            <ArrowIcon />
          </div>
        </button>
      )}

      {!isEnd && (
        <button
          onClick={() => swiper.slideNext()}
          className="absolute right-2 sm:right-[24px] top-1/2 -translate-y-1/2 z-10 sm:w-8 sm:h-8 w-5 h-5 rounded-full bg-[#EFE8DF] flex items-center justify-center cursor-pointer"
        >
          <ArrowIcon />
        </button>
      )}
    </>
  );
};

const InstagramPostsSlider = () => {
  const { t } = useTranslation('home');
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = slides[activeIndex];

  return (
    <section className="instagram_section !py-8 sm:!py-10 lg:!py-[60px]">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-10">
        <h2 className="section-heading text-center mb-5 sm:mb-8 lg:mb-11 max-w-[370px] sm:max-w-full mx-auto">{t('instagram.heading')}</h2>

        <div className="flex gap-4 sm:gap-3 lg:gap-4 lg:flex-row sm:flex-col flex-col-reverse lg:min-h-[572px]">
          <div className="flex lg:flex-col gap-3 lg:gap-4 overflow-x-auto no-scrollbar">
            {slides.map((slide, i) => (
              <div
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`min-w-[170px] sm:min-w-[230px] lg:min-w-[282px] max-w-fit sm:max-w-[230px] lg:max-w-[282px] rounded-lg sm:rounded-2xl lg:p-5 sm:p-4 p-2.5 flex flex-col justify-start cursor-pointer transition-colors duration-200 ${i === slides.length - 1 ? 'lg:h-[82px] sm:h-[93px]' : 'lg:h-[106.5px] sm:h-[93px]'
                  } ${i === activeIndex
                    ? 'bg-[#9B2626] text-white'
                    : 'bg-[#FCFAF8] text-black shadow-[0_2px_10px_0_rgba(0,0,0,0.06)]'
                  }`}
              >
                <div className="text-xs sm:text-base lg:text-lg font-semibold mb-1.5 leading-none uppercase whitespace-nowrap">
                  {t(slide.nameKey)}
                </div>

                <div className="sm:text-sm text-[9px] lg:font-medium uppercase">
                  {t('instagram.atLocation', { location: slide.location })}
                </div>
              </div>
            ))}
          </div>

          <div className="w-full flex-1 sm:rounded-[28px] rounded-lg shadow-[0_2px_5px_0_rgba(0,0,0,0.06)] sm:shadow-[0_6px_20px_0_rgba(0,0,0,0.12)] max-w-[1222px] overflow-hidden">
            <div key={activeIndex} className="animate-slideFadeIn flex sm:flex-col flex-col-reverse flex-1">
              <a
                href={activeSlide.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center lg:gap-4 sm:gap-2 gap-1.5 px-2.5 sm:px-[18px] lg:px-[30px] lg:py-5 sm:py-[18px] py-1.5 w-full"
              >
                <div className="lg:w-[88px] w-9 sm:w-[60px] lg:h-[88px] h-9 sm:h-[60px] rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img
                    src={activeSlide.avatar}
                    alt={t(activeSlide.nameKey)}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex flex-col lg:gap-1">
                  <div className="flex items-center lg:gap-2 gap-1">
                    <span className="font-semibold text-xs sm:text-sm lg:text-[24px] text-black uppercase">
                      {t(activeSlide.nameKey)}
                    </span>

                    <VerifiedBadge />
                  </div>

                  <span className="text-[10px] sm:text-xs lg:text-lg text-black">
                    {activeSlide.handle}
                  </span>
                </div>
              </a>

              <div className="relative lg:p-[30px] p-2.5 sm:p-[18px] lg:pt-[72px] sm:pt-[30px] pr-0 lg:-mt-[50px] sm:-mt-6 flex-1 w-full overflow-hidden bg-[linear-gradient(to_top,transparent,#EFE8DF_50px)] sm:bg-[linear-gradient(to_bottom,transparent,#EFE8DF_50px)]">
                <Swiper
                  slidesPerView="auto"
                  spaceBetween={16}
                  breakpoints={{
                    1024: {
                      spaceBetween: 24,
                    },
                  }}
                  className="h-full"
                >
                  {activeSlide.images.map((img, i) => (
                    <SwiperSlide key={i} className='lg:w-[301px] sm:w-[152px] w-[128px]'>
                      <div className="lg:w-[301px] lg:h-[392px] sm:w-[152px] w-[128px] sm:h-[198px] h-[166px] sm:rounded-[14.9px] rounded-[6.73px] overflow-hidden select-none">
                        <img
                          src={img}
                          alt={`${t(activeSlide.nameKey)} - ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </SwiperSlide>
                  ))}

                  <SliderNavButtons />
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstagramPostsSlider;
